<template>
  <section class="my-team-page">
    <PageHeader :title="myTeamTitle" icon="uil-users-alt">
      <template #right>
        <span class="team-count">
          {{ $t('general.employee_count', { count: loading ? '—' : total }) }}
        </span>
      </template>
    </PageHeader>

    <div class="team-toolbar">
      <p>{{ $t('general.my_team_description') }}</p>
      <multiple-select-input v-model="selectedSort"
                             :allowEmpty="false"
                             :multiple="false"
                             :required="false"
                             :searchable="false"
                             :showLabels="false"
                             :values="sortOptions"
                             class="team-sort"
                             @select="onSortChange"
      />
    </div>

    <TeamListSkeleton v-if="loading" :count="skeletonCount"/>

    <div v-else-if="errorMessage" class="team-state team-state--error" role="alert">
      <i aria-hidden="true" class="uil-exclamation-triangle"></i>
      <div>
        <strong>{{ $t('general.team_load_failed') }}</strong>
        <p>{{ errorMessage }}</p>
      </div>
      <button class="team-retry" type="button" @click="loadTeam">
        {{ $t('general.try_again') }}
      </button>
    </div>

    <div v-else-if="!team.length" class="team-state team-state--empty">
      <i aria-hidden="true" class="uil-users-alt"></i>
      <div>
        <strong>{{ $t('general.no_direct_reports') }}</strong>
        <p>{{ $t('general.no_direct_reports_hint') }}</p>
      </div>
    </div>

    <template v-else>
      <div class="team-table-card team-table-card--desktop">
        <div class="team-table-scroll">
          <table class="team-table">
            <thead>
            <tr>
              <th scope="col">{{ $t('general.employee') }}</th>
              <th scope="col">{{ $t('profile.gm_region') }}</th>
              <th scope="col">{{ $t('profile.unit') }}</th>
              <th scope="col">{{ $t('profile.branch') }}</th>
              <th scope="col">{{ $t('users.mission') }}</th>
              <th scope="col">{{ $t('profile.title') }}</th>
              <th class="team-table__count" scope="col">{{ $t('general.total_competencies') }}</th>
              <th class="team-table__action" scope="col">{{ $t('general.action') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="employee in team" :key="employee.id">
              <td>
                <div class="team-person">
                  <UserProfileAvatar
                      :name="employee.full_name || ''"
                      :photo="employee.avatar || ''"
                      class="team-person-avatar"
                  />
                  <strong>{{ employee.full_name || "—" }}</strong>
                </div>
              </td>
              <td>{{ displayValue(employee.region_name) }}</td>
              <td>{{ displayValue(employee.department_name) }}</td>
              <td>{{ displayValue(employee.branch_name) }}</td>
              <td>{{ displayValue(employee.position_name) }}</td>
              <td>{{ displayValue(employee.title_name) }}</td>
              <td class="team-table__count">
                <span class="competency-count">{{ Number(employee.competencies_count) || 0 }}</span>
              </td>
              <td class="team-table__action">
                <div class="team-actions">
                  <router-link
                      :aria-label="$t('general.view_employee_competencies', { name: employee.full_name || $t('general.employee') })"
                      :to="{
                        name: 'user.my-team.member',
                        params: { userId: String(employee.id) },
                      }"
                      class="team-detail-link"
                  >
                    {{ $t('general.competencies') }}
                    <i aria-hidden="true" class="uil-arrow-right"></i>
                  </router-link>
                  <button
                      :aria-label="$t('general.view_employee_training', { name: employee.full_name || $t('general.employee') })"
                      class="team-training-button"
                      type="button"
                      @click="openTrainingStatus(employee)"
                  >
                    <i aria-hidden="true" class="uil-graduation-hat"></i>
                    {{ $t('general.training_status') }}
                  </button>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <TeamCardList :team="team" @open-training-status="openTrainingStatus"/>

      <Pagination v-if="totalPages > 1"
                  :pagination="pagination"
                  class="team-pagination"
                  @changePage="changePage"
      />
    </template>

    <EducationalStatusModal :employee="selectedEmployee"
                            :visible.sync="trainingModalVisible"
                            @close="selectedEmployee = null"
    />
  </section>
</template>

<script>
import PageHeader from "@/view/components/common/PageHeader.vue";
import UserProfileAvatar from "@/view/components/common/UserProfileAvatar.vue";
import EducationalStatusModal from "@/view/User/MyTeam/components/EducationalStatusModal.vue";
import TeamCardList from "@/view/User/MyTeam/components/TeamCardList.vue";
import TeamListSkeleton from "@/view/User/MyTeam/components/TeamListSkeleton.vue";
import MultipleSelectInput from "@/assets/components/inputs/MultipleSelectInput.vue";
import Pagination from "@/assets/components/Pagination.vue";
import ApiService from "@/core/services/api.service";
import querystring from "querystring";
import widgetMixin from "@/core/mixin/widget.mixin";

export default {
  name: "MyTeam",
  mixins: [widgetMixin],
  components: {
    UserProfileAvatar,
    EducationalStatusModal,
    MultipleSelectInput,
    PageHeader,
    Pagination,
    TeamCardList,
    TeamListSkeleton,
  },
  data() {
    return {
      team: [],
      total: 0,
      selectedSort: null,
      page: 1,
      perPage: 15,
      totalPages: 1,
      loading: true,
      errorMessage: "",
      requestSequence: 0,
      trainingModalVisible: false,
      selectedEmployee: null,
    };
  },
  computed: {
    myTeamTitle() {
      const sourceMenu = this.getDashboardMenuItems(this.menuListMixin);
      const menuItem = sourceMenu.find(item => ["my_team", "my-team"].includes(item.type));
      return this.getLocalizedWidgetTitle(menuItem, this.$t("general.my_team"), this.$i18n?.locale);
    },
    skeletonCount() {
      return Math.min(this.perPage, 6);
    },
    sortOptions() {
      return [
        { id: "desc", name: this.$t("general.sort_highest_competency") },
        { id: "asc", name: this.$t("general.sort_lowest_competency") },
      ];
    },
    pagination() {
      return {
        current_page: this.page,
        last_page: this.totalPages,
        per_page: this.perPage,
        total: this.total,
      };
    },
  },
  created() {
    this.syncSelectedSort();
  },
  async mounted() {
    // Widget kapalıysa sayfa erişime kapalı (sol menü ile aynı kural)
    try {
      await this.getWidgetsMixin();

      if (!this.isMyTeamActiveMixin) {
        await this.$router.push({ path: "/dashboard" });
        return;
      }

      await this.loadTeam();
    } catch (error) {
      await this.$router.push({ path: "/dashboard" });
    }
  },
  beforeDestroy() {
    this.requestSequence += 1;
  },
  methods: {
    syncSelectedSort() {
      const currentId = this.selectedSort?.id || "desc";
      this.selectedSort =
          this.sortOptions.find((option) => option.id === currentId) ||
          this.sortOptions[0];
    },
    onSortChange(option) {
      this.selectedSort = option || this.sortOptions[0];
      this.page = 1;
      this.loadTeam();
    },
    async loadTeam() {
      const requestId = ++this.requestSequence;
      this.loading = true;
      this.errorMessage = "";

      try {
        const response = await ApiService.get(
            `api/my-team?${querystring.stringify({
              order_by: "competencies_count",
              sort: this.selectedSort?.id || "desc",
              page: this.page,
              perPage: this.perPage,
            })}`
        );

        if (requestId !== this.requestSequence) return;

        const payload = response?.data || {};
        const meta = payload.meta || {};
        const total = Number(meta.total);

        this.team = Array.isArray(payload.data)
            ? payload.data.filter(
                (employee) =>
                    employee &&
                    typeof employee === "object" &&
                    employee.id !== undefined &&
                    employee.id !== null
            )
            : [];
        this.total = Number.isFinite(total) ? total : this.team.length;
        this.page = Number(meta.current_page) || this.page;
        this.totalPages = Math.max(
            1,
            Number(meta.last_page) ||
            Math.ceil(this.total / this.perPage) ||
            1
        );
      } catch (error) {
        if (requestId !== this.requestSequence) return;
        this.team = [];
        this.total = 0;
        this.totalPages = 1;
        this.errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            this.$t("general.try_later");
      } finally {
        if (requestId === this.requestSequence) this.loading = false;
      }
    },
    changePage(page) {
      const nextPage = Number(page);
      if (
          !Number.isFinite(nextPage) ||
          nextPage < 1 ||
          nextPage > this.totalPages ||
          nextPage === this.page
      ) {
        return;
      }
      this.page = nextPage;
      this.loadTeam();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    openTrainingStatus(employee) {
      if (!employee || employee.id === null || employee.id === undefined) return;
      this.selectedEmployee = { ...employee };
      this.trainingModalVisible = true;
    },
    displayValue(value) {
      return value === undefined || value === null || String(value).trim() === ""
          ? "—"
          : String(value);
    },
  },
};
</script>

<style scoped>
.my-team-page {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  padding: 8px 0 28px;
  box-sizing: border-box;
  overflow-x: hidden;
}

.team-count {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 0 11px;
  border-radius: 999px;
  color: var(--backgroundColor, #666cff);
  background: rgba(102, 108, 255, 0.1);
  font-size: 13px;
  font-weight: 600;
}

.team-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px;
  margin-bottom: 18px;
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 12px;
  box-sizing: border-box;
}

.team-toolbar p {
  margin: 0;
  color: #64748b;
  font-size: 14px;
  min-width: 0;
}

.team-sort {
  flex: 0 0 240px;
  width: 240px;
  max-width: 240px;
  margin: 0;
}

.team-sort :deep(.form-group) {
  margin: 0;
}

.team-sort :deep(.multiselect) {
  height: 42px !important;
  min-height: 42px !important;
  max-height: 42px !important;
}

.team-sort :deep(.multiselect__tags) {
  box-sizing: border-box;
  height: 42px !important;
  min-height: 42px !important;
  max-height: 42px !important;
  padding: 8px 40px 0 12px;
  overflow: hidden;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
}

.team-sort :deep(.multiselect__select) {
  height: 40px;
}

.team-sort :deep(.multiselect__placeholder),
.team-sort :deep(.multiselect__single) {
  box-sizing: border-box !important;
  display: block !important;
  width: 100% !important;
  max-width: 100% !important;
  height: 24px !important;
  min-height: 24px !important;
  margin: 0 0 8px !important;
  padding: 0 !important;
  overflow: hidden !important;
  border: 0 !important;
  background: transparent !important;
  color: #1e293b !important;
  font-size: 13px !important;
  font-weight: 400 !important;
  line-height: 24px !important;
  white-space: nowrap !important;
  text-overflow: ellipsis !important;
}

.team-sort :deep(.multiselect__input) {
  position: absolute !important;
  width: 0 !important;
  min-width: 0 !important;
  height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  border: 0 !important;
  overflow: hidden !important;
}

.team-sort :deep(.multiselect--active .multiselect__tags) {
  border-color: var(--backgroundColor, #666cff);
  box-shadow: 0 0 0 3px rgba(102, 108, 255, 0.1);
}

.team-table-card {
  overflow: hidden;
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 12px;
  box-shadow: 0 4px 18px rgba(30, 41, 59, 0.04);
}

.team-pagination {
  margin-top: 18px;
}

.team-table-scroll {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.team-table {
  width: 100%;
  min-width: 1100px;
  border-collapse: collapse;
  color: #475569;
  font-size: 13px;
}

.team-table th,
.team-table td {
  padding: 14px 13px;
  border-bottom: 1px solid #edf1f6;
  text-align: left;
  vertical-align: middle;
  white-space: nowrap;
}

.team-table th {
  color: #64748b;
  background: #f8fafc;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.team-table tbody tr:last-child td {
  border-bottom: 0;
}

.team-table tbody tr:hover {
  background: #fafbff;
}

.team-table a {
  color: var(--backgroundColor, #666cff);
}

.team-person {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 180px;
}

.team-person-avatar {
  width: 38px;
  height: 38px;
  min-width: 38px;
  flex-shrink: 0;
  font-size: 13px;
}

.team-person strong {
  color: #1e293b;
  font-weight: 600;
}

.team-table__count {
  text-align: center !important;
}

.team-table__action {
  text-align: right !important;
}

.team-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 7px;
  flex-wrap: wrap;
}

.team-detail-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 34px;
  padding: 0 11px;
  border: 1px solid rgba(102, 108, 255, 0.25);
  border-radius: 8px;
  color: var(--backgroundColor, #666cff) !important;
  background: rgba(102, 108, 255, 0.07);
  font-weight: 600;
  text-decoration: none;
}

.team-detail-link:hover,
.team-detail-link:focus-visible {
  border-color: var(--backgroundColor, #666cff);
  background: rgba(102, 108, 255, 0.12);
  outline: none;
}

.team-training-button {
  display: inline-flex;
  min-height: 34px;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 0 11px;
  margin: 0;
  color: #0f766e;
  background: #f0fdfa;
  border: 1px solid #99f6e4;
  border-radius: 8px;
  box-shadow: none;
  font: inherit;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  appearance: none;
}

.team-training-button:hover,
.team-training-button:focus-visible {
  color: #0f5f59;
  background: #ccfbf1;
  border-color: #2dd4bf;
  outline: none;
}

.competency-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 30px;
  padding: 0 8px;
  border-radius: 999px;
  color: var(--backgroundColor, #666cff);
  background: rgba(102, 108, 255, 0.1);
  font-weight: 700;
}

.team-state {
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px;
  color: #64748b;
  text-align: center;
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 12px;
}

.team-state > i {
  color: #94a3b8;
  font-size: 36px;
}

.team-state strong {
  display: block;
  color: #334155;
}

.team-state p {
  margin: 4px 0 0;
}

.team-state--error {
  flex-wrap: wrap;
}

.team-state--error > i {
  color: #dc2626;
}

.team-retry {
  padding: 9px 14px;
  border: 0;
  border-radius: 8px;
  color: #fff;
  background: var(--backgroundColor, #666cff);
  cursor: pointer;
}

@media (max-width: 800px) {
  .my-team-page {
    padding: 4px 0 20px;
  }

  .team-toolbar {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    padding: 12px;
  }

  .team-sort {
    flex: 1 1 auto;
    width: 100%;
    max-width: none;
  }

  .team-table-card--desktop {
    display: none;
  }

  .team-state {
    min-height: 180px;
    flex-direction: column;
    padding: 24px 16px;
  }
}

@media (max-width: 800px) {
  .my-team-page {
    padding: 0 12px 20px;
  }

  .my-team-page :deep(.page-header-block) {
    gap: 8px;
  }

  .my-team-page :deep(.page-header-block__left),
  .my-team-page :deep(.page-header-block__title) {
    min-width: 0;
  }

  .my-team-page :deep(.page-header-block__title) {
    font-size: 18px;
  }

  .team-count {
    min-height: 40px;
    padding: 0 10px;
    font-size: 12px;
    white-space: nowrap;
  }

  .team-toolbar {
    gap: 12px;
    margin-bottom: 16px;
    padding: 12px;
    border-radius: 12px;
  }

  .team-toolbar p {
    font-size: 14px;
    line-height: 1.5;
  }

  .team-sort :deep(.multiselect),
  .team-sort :deep(.multiselect__tags) {
    height: 48px !important;
    min-height: 48px !important;
    max-height: 48px !important;
  }

  .team-sort :deep(.multiselect__tags) {
    padding: 12px 40px 12px 16px;
    border-radius: 10px;
  }

  .team-sort :deep(.multiselect__single),
  .team-sort :deep(.multiselect__placeholder) {
    height: 24px !important;
    margin-bottom: 0 !important;
    font-size: 16px !important;
    line-height: 24px !important;
  }

  .team-state {
    width: 100%;
    box-sizing: border-box;
    gap: 10px;
    padding: 32px 16px;
    border-radius: 12px;
  }

  .team-retry {
    min-height: 48px;
    padding: 12px 16px;
  }

  .team-pagination {
    max-width: 100%;
    overflow-x: auto;
  }
}

@media (max-width: 480px) {
  .my-team-page :deep(.page-header-block) {
    flex-wrap: nowrap;
    align-items: center;
    gap: 6px;
  }

  .my-team-page :deep(.page-header-block__left) {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
  }

  .my-team-page :deep(.page-header-block__title) {
    display: flex;
    width: 100%;
    min-width: 0;
    font-size: 16px;
    line-height: 1.25;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .my-team-page :deep(.page-header-block__right) {
    flex: 0 0 auto;
    width: auto;
    min-width: 0;
    justify-content: flex-end;
  }

  .team-count {
    min-height: 36px;
    max-width: 46vw;
    padding: 0 8px;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 11px;
  }
}
</style>
